import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const LaunchWarRoom = ({ apiBase }) => {
  const { tenant } = useTenant();

  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [velocity, setVelocity] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLaunchData();
  }, []);

  const fetchLaunchData = async () => {
    try {
      // Get all strategies
      const strategiesRes = await axios.get(`${apiBase}/sales-strategy`);
      console.log('All strategies:', strategiesRes.data);

      // Filter for launch strategies - be more flexible with matching
      let launchStrategies = strategiesRes.data.filter(s => 
        s.strategy_name?.toLowerCase().includes('launch') || 
        s.strategy_name?.toLowerCase().includes('war room') ||
        s.strategy_name?.toLowerCase().includes('war room plan') ||
        s.strategy_name?.toLowerCase().includes('jan 15') ||
        s.strategy_name?.toLowerCase().includes('mar 15') ||
        s.strategy_name?.toLowerCase().includes('pre-launch')
      );

      // If no launch strategies found, show all strategies (for debugging)
      if (launchStrategies.length === 0 && strategiesRes.data.length > 0) {
        console.warn('No launch strategies found. Showing all strategies:', strategiesRes.data);
        launchStrategies = strategiesRes.data;
      }

      setStrategies(launchStrategies);
      
      if (launchStrategies.length > 0) {
        const mainStrategy = launchStrategies[0];
        console.log('Selected strategy:', mainStrategy);
        
        // Fetch complete strategy details (includes activities, meetings, velocity, team)
        try {
          const strategyDetailsRes = await axios.get(`${apiBase}/sales-strategy/${mainStrategy.id}`);
          const details = strategyDetailsRes.data;
          console.log('Strategy details:', details);
          
          setSelectedStrategy(details.strategy || mainStrategy);
          setActivities(details.activities || []);
          setMeetings(details.meetings || []);
          setVelocity(details.velocity || []);
          setTeamAssignments(details.team || []);
        } catch (err) {
          // If detailed fetch fails, use basic strategy data
          setSelectedStrategy(mainStrategy);
          console.error('Error fetching strategy details:', err);
        }
      } else {
        console.warn('No strategies found at all');
      }
    } catch (error) {
      console.error('Error fetching launch data:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading Launch War Room...</div>;
  }

  const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  const parseNotes = (notes) => {
    if (!notes) return { owner: null, sirResponsibility: null };
    // Handle format: "Owner: Name | Sir Responsibility: Responsibility"
    const ownerMatch = notes.match(/Owner:\s*([^|]+)/i);
    const sirMatch = notes.match(/Sir Responsibility:\s*(.+?)(?:\s*\||$)/i);
    return {
      owner: ownerMatch ? ownerMatch[1].trim() : null,
      sirResponsibility: sirMatch ? sirMatch[1].trim() : null
    };
  };

  const atlActivities = activities.filter(a => a.activity_type === 'ATL');
  const btlActivities = activities.filter(a => a.activity_type === 'BTL');
  const digitalActivities = activities.filter(a => a.activity_type === 'Digital');
  const operationsActivities = activities.filter(a => a.activity_type === 'Operations');
  const legalActivities = activities.filter(a => a.activity_type === 'Legal');
  const strategyActivities = activities.filter(a => a.activity_type === 'Strategy');

  const totalBudget = activities.reduce((sum, a) => sum + (a.budget || 0), 0);
  const atlBudget = atlActivities.reduce((sum, a) => sum + (a.budget || 0), 0);
  const btlBudget = btlActivities.reduce((sum, a) => sum + (a.budget || 0), 0);
  const digitalBudget = digitalActivities.reduce((sum, a) => sum + (a.budget || 0), 0);
  const operationsBudget = operationsActivities.reduce((sum, a) => sum + (a.budget || 0), 0);
  const legalBudget = legalActivities.reduce((sum, a) => sum + (a.budget || 0), 0);
  const strategyBudget = strategyActivities.reduce((sum, a) => sum + (a.budget || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ marginBottom: '8px', color: '#1e40af' }}>🚀 Project Launch War Room</h1>
          <p style={{ color: '#64748b', fontSize: '16px' }}>
            {selectedStrategy?.strategy_name || tenant.name} - Pre-Launch: Jan 15 to Mar 15, 2026
          </p>
        </div>
        <a href="/" className="btn btn-secondary" style={{ textDecoration: 'none' }}>
          ← Back to Dashboard
        </a>
      </div>

      {!selectedStrategy ? (
        <div className="card">
          <h2>No Launch Strategy Found</h2>
          <p>Please create a launch strategy first by running the script:</p>
          <div style={{ background: '#f3f4f6', padding: '16px', borderRadius: '8px', marginTop: '16px' }}>
            <p><strong>Steps to create:</strong></p>
            <ol style={{ marginLeft: '20px' }}>
              <li>Find your project ID: <code>sqlite3 server/database/sir_sales.db "SELECT id, name FROM projects;"</code></li>
              <li>Update <code>create_war_room_plan_jan_mar.js</code> with your project ID</li>
              <li>Run: <code>node create_war_room_plan_jan_mar.js</code></li>
              <li>Refresh this page</li>
            </ol>
            <p style={{ marginTop: '16px' }}>
              <strong>Or</strong> create a strategy manually in the <a href="/sales-strategy">Sales Strategy</a> page.
            </p>
          </div>
          {strategies.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <p><strong>Available strategies:</strong></p>
              <ul>
                {strategies.map(s => (
                  <li key={s.id}>{s.strategy_name} (ID: {s.id})</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Strategy Overview */}
          <div className="card" style={{ marginBottom: '24px', border: '2px solid #2563eb', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}>
            <h2 className="card-title" style={{ color: '#1e40af' }}>Launch Strategy Overview</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Strategy Name</div>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e40af', marginTop: '4px' }}>
                  {selectedStrategy.strategy_name}
                </div>
              </div>
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Target Sales</div>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e40af', marginTop: '4px' }}>
                  {selectedStrategy.target_sales || 0} units
                </div>
              </div>
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Target Revenue</div>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e40af', marginTop: '4px' }}>
                  ₹{formatCurrency(selectedStrategy.target_revenue || 0)}
                </div>
              </div>
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Duration</div>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e40af', marginTop: '4px' }}>
                  {new Date(selectedStrategy.start_date).toLocaleDateString()} - {new Date(selectedStrategy.end_date).toLocaleDateString()}
                </div>
              </div>
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Total Activities</div>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e40af', marginTop: '4px' }}>
                  {activities.length}
                </div>
              </div>
              <div style={{ padding: '16px', background: 'white', borderRadius: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Total Budget</div>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e40af', marginTop: '4px' }}>
                  {totalBudget > 0 ? `₹${formatCurrency(totalBudget)}` : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Budget Breakdown */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 className="card-title">Budget Breakdown</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div style={{ padding: '16px', background: '#2563eb10', borderRadius: '8px', border: '1px solid #2563eb' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>ATL Budget</div>
                <div style={{ fontWeight: '600', fontSize: '20px', color: '#2563eb', marginTop: '4px' }}>
                  {atlBudget > 0 ? `₹${formatCurrency(atlBudget)}` : '-'}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  {atlActivities.length} activities
                </div>
              </div>
              <div style={{ padding: '16px', background: '#10b98110', borderRadius: '8px', border: '1px solid #10b981' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>BTL Budget</div>
                <div style={{ fontWeight: '600', fontSize: '20px', color: '#10b981', marginTop: '4px' }}>
                  {btlBudget > 0 ? `₹${formatCurrency(btlBudget)}` : '-'}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  {btlActivities.length} activities
                </div>
              </div>
              <div style={{ padding: '16px', background: '#f59e0b10', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                <div style={{ color: '#64748b', fontSize: '14px' }}>Digital Budget</div>
                <div style={{ fontWeight: '600', fontSize: '20px', color: '#f59e0b', marginTop: '4px' }}>
                  {digitalBudget > 0 ? `₹${formatCurrency(digitalBudget)}` : '-'}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  {digitalActivities.length} activities
                </div>
              </div>
              {operationsActivities.length > 0 && (
                <div style={{ padding: '16px', background: '#8b5cf610', borderRadius: '8px', border: '1px solid #8b5cf6' }}>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>Operations Budget</div>
                  <div style={{ fontWeight: '600', fontSize: '20px', color: '#8b5cf6', marginTop: '4px' }}>
                    {operationsBudget > 0 ? `₹${formatCurrency(operationsBudget)}` : '-'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    {operationsActivities.length} activities
                  </div>
                </div>
              )}
              {legalActivities.length > 0 && (
                <div style={{ padding: '16px', background: '#ec489910', borderRadius: '8px', border: '1px solid #ec4899' }}>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>Legal Budget</div>
                  <div style={{ fontWeight: '600', fontSize: '20px', color: '#ec4899', marginTop: '4px' }}>
                    {legalBudget > 0 ? `₹${formatCurrency(legalBudget)}` : '-'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    {legalActivities.length} activities
                  </div>
                </div>
              )}
              {strategyActivities.length > 0 && (
                <div style={{ padding: '16px', background: '#06b6d410', borderRadius: '8px', border: '1px solid #06b6d4' }}>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>Strategy Budget</div>
                  <div style={{ fontWeight: '600', fontSize: '20px', color: '#06b6d4', marginTop: '4px' }}>
                    {strategyBudget > 0 ? `₹${formatCurrency(strategyBudget)}` : '-'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    {strategyActivities.length} activities
                  </div>
                </div>
              )}
            </div>
            {totalBudget > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'ATL', value: atlBudget },
                      { name: 'BTL', value: btlBudget },
                      { name: 'Digital', value: digitalBudget },
                      { name: 'Operations', value: operationsBudget },
                      { name: 'Legal', value: legalBudget },
                      { name: 'Strategy', value: strategyBudget }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[COLORS[0], COLORS[1], COLORS[2], COLORS[3], COLORS[4], COLORS[5]].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₹${formatCurrency(value)}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                <p>Budget amounts not specified</p>
              </div>
            )}
          </div>

          {/* Sales Velocity Chart */}
          {velocity.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">Sales Velocity Targets</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={velocity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="target_sales" fill="#2563eb" name="Target Sales" />
                  <Bar dataKey="actual_sales" fill="#10b981" name="Actual Sales" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ATL Activities */}
          {atlActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">📺 ATL (Above The Line) Activities</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Owner</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atlActivities.map((activity, idx) => {
                      const notes = parseNotes(activity.notes);
                      return (
                        <tr key={idx}>
                          <td><strong>{activity.activity_name}</strong></td>
                          <td>{activity.category}</td>
                          <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                          <td>{new Date(activity.end_date).toLocaleDateString()}</td>
                          <td>{activity.budget > 0 ? `₹${formatCurrency(activity.budget)}` : '-'}</td>
                          <td>
                            {notes.owner && (
                              <div style={{ fontSize: '12px' }}>
                                <div><strong>Owner:</strong> {notes.owner}</div>
                                {notes.sirResponsibility && (
                                  <div style={{ color: '#ef4444', marginTop: '4px' }}>
                                    <strong>Sir:</strong> {notes.sirResponsibility}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="badge" style={{ 
                              backgroundColor: activity.priority === 'high' ? '#ef444420' : 
                                              activity.priority === 'critical' ? '#dc262620' : '#6b728020',
                              color: activity.priority === 'high' ? '#ef4444' : 
                                     activity.priority === 'critical' ? '#dc2626' : '#6b7280'
                            }}>
                              {activity.priority}
                            </span>
                          </td>
                          <td>
                            <span className="badge" style={{ 
                              backgroundColor: activity.status === 'completed' ? '#10b98120' : 
                                              activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                              color: activity.status === 'completed' ? '#10b981' : 
                                     activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                            }}>
                              {activity.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* BTL Activities */}
          {btlActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">🎯 BTL (Below The Line) Activities</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {btlActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td><strong>{activity.activity_name}</strong></td>
                        <td>{activity.category}</td>
                        <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                        <td>{new Date(activity.end_date).toLocaleDateString()}</td>
                        <td>{activity.budget > 0 ? `₹${formatCurrency(activity.budget)}` : '-'}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.priority === 'high' ? '#ef444420' : 
                                            activity.priority === 'critical' ? '#dc262620' : '#6b728020',
                            color: activity.priority === 'high' ? '#ef4444' : 
                                   activity.priority === 'critical' ? '#dc2626' : '#6b7280'
                          }}>
                            {activity.priority}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.status === 'completed' ? '#10b98120' : 
                                            activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                            color: activity.status === 'completed' ? '#10b981' : 
                                   activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                          }}>
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Digital Activities */}
          {digitalActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">💻 Digital Marketing Activities</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {digitalActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td><strong>{activity.activity_name}</strong></td>
                        <td>{activity.category}</td>
                        <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                        <td>{new Date(activity.end_date).toLocaleDateString()}</td>
                        <td>{activity.budget > 0 ? `₹${formatCurrency(activity.budget)}` : '-'}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.priority === 'high' ? '#ef444420' : 
                                            activity.priority === 'critical' ? '#dc262620' : '#6b728020',
                            color: activity.priority === 'high' ? '#ef4444' : 
                                   activity.priority === 'critical' ? '#dc2626' : '#6b7280'
                          }}>
                            {activity.priority}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.status === 'completed' ? '#10b98120' : 
                                            activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                            color: activity.status === 'completed' ? '#10b981' : 
                                   activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                          }}>
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Operations Activities */}
          {operationsActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">⚙️ Operations Activities</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operationsActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td><strong>{activity.activity_name}</strong></td>
                        <td>{activity.category}</td>
                        <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                        <td>{new Date(activity.end_date).toLocaleDateString()}</td>
                        <td>{activity.budget > 0 ? `₹${formatCurrency(activity.budget)}` : '-'}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.priority === 'high' ? '#ef444420' : 
                                            activity.priority === 'critical' ? '#dc262620' : '#6b728020',
                            color: activity.priority === 'high' ? '#ef4444' : 
                                   activity.priority === 'critical' ? '#dc2626' : '#6b7280'
                          }}>
                            {activity.priority}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.status === 'completed' ? '#10b98120' : 
                                            activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                            color: activity.status === 'completed' ? '#10b981' : 
                                   activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                          }}>
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Legal Activities */}
          {legalActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">⚖️ Legal & Compliance Activities</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legalActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td><strong>{activity.activity_name}</strong></td>
                        <td>{activity.category}</td>
                        <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                        <td>{new Date(activity.end_date).toLocaleDateString()}</td>
                        <td>{activity.budget > 0 ? `₹${formatCurrency(activity.budget)}` : '-'}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.priority === 'high' ? '#ef444420' : 
                                            activity.priority === 'critical' ? '#dc262620' : '#6b728020',
                            color: activity.priority === 'high' ? '#ef4444' : 
                                   activity.priority === 'critical' ? '#dc2626' : '#6b7280'
                          }}>
                            {activity.priority}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.status === 'completed' ? '#10b98120' : 
                                            activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                            color: activity.status === 'completed' ? '#10b981' : 
                                   activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                          }}>
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Strategy Activities */}
          {strategyActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">📊 Strategy Activities</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {strategyActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td><strong>{activity.activity_name}</strong></td>
                        <td>{activity.category}</td>
                        <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                        <td>{new Date(activity.end_date).toLocaleDateString()}</td>
                        <td>{activity.budget > 0 ? `₹${formatCurrency(activity.budget)}` : '-'}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.priority === 'high' ? '#ef444420' : 
                                            activity.priority === 'critical' ? '#dc262620' : '#6b728020',
                            color: activity.priority === 'high' ? '#ef4444' : 
                                   activity.priority === 'critical' ? '#dc2626' : '#6b7280'
                          }}>
                            {activity.priority}
                          </span>
                        </td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: activity.status === 'completed' ? '#10b98120' : 
                                            activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                            color: activity.status === 'completed' ? '#10b981' : 
                                   activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                          }}>
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Channel Partner Meetings */}
          {meetings.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">🤝 Channel Partner Meetings</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Meeting Type</th>
                      <th>Date & Time</th>
                      <th>Location</th>
                      <th>Agenda</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meetings.map((meeting, idx) => (
                      <tr key={idx}>
                        <td><strong>{meeting.meeting_type}</strong></td>
                        <td>{new Date(meeting.scheduled_date).toLocaleString()}</td>
                        <td>{meeting.location || '-'}</td>
                        <td>{meeting.agenda || '-'}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: meeting.status === 'completed' ? '#10b98120' : '#2563eb20',
                            color: meeting.status === 'completed' ? '#10b981' : '#2563eb'
                          }}>
                            {meeting.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Team Assignments */}
          {teamAssignments.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">👥 Team Assignments</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Team Member</th>
                      <th>Role</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamAssignments.map((assignment, idx) => (
                      <tr key={idx}>
                        <td><strong>{assignment.user_name || assignment.assigned_to}</strong></td>
                        <td>{assignment.role || '-'}</td>
                        <td>{new Date(assignment.start_date).toLocaleDateString()}</td>
                        <td>{new Date(assignment.end_date).toLocaleDateString()}</td>
                        <td>
                          <span className="badge" style={{ 
                            backgroundColor: assignment.status === 'active' ? '#10b98120' : '#6b728020',
                            color: assignment.status === 'active' ? '#10b981' : '#6b7280'
                          }}>
                            {assignment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LaunchWarRoom;

